import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEXTES = {
  fr: {
    seoTitre: 'Contact - AfriOne BD, épicerie africaine à Liège',
    seoDescription: 'Une question sur votre commande ou nos produits africains ? Contactez AfriOne BD : contact@afrionebd.eu, 0465 71 01 17, Rue Saint-Nicolas 227, 4000 Liège.',
    coordonnees: 'Nos coordonnées',
    horaires: "Horaires d'ouverture",
    semaine: 'Lundi - Vendredi',
    weekend: 'Samedi - Dimanche',
    envoyezMessage: 'Envoyez-nous un message',
    reponse: 'Nous vous répondrons dans les plus brefs délais.',
    autreMessage: 'Envoyer un autre message',
    erreur: 'Une erreur est survenue. Veuillez réessayer.',
  },
  en: {
    seoTitre: 'Contact - AfriOne BD, African grocery in Liège',
    seoDescription: 'A question about your order or our African products? Contact AfriOne BD: contact@afrionebd.eu, 0465 71 01 17, Rue Saint-Nicolas 227, 4000 Liège.',
    coordonnees: 'Our details',
    horaires: 'Opening hours',
    semaine: 'Monday - Friday',
    weekend: 'Saturday - Sunday',
    envoyezMessage: 'Send us a message',
    reponse: 'We will get back to you as soon as possible.',
    autreMessage: 'Send another message',
    erreur: 'Something went wrong. Please try again.',
  },
};

export default function Contact() {
  const { t, language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success(t('messageSent'));
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      // Error handled silently
      toast.error(x.erreur);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 overflow-x-hidden" data-testid="contact-page">
      <SEO
        title={x.seoTitre}
        description={x.seoDescription}
        canonical="https://afrionebd.eu/contact"
      />
      {/* Header */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-secondary/30 to-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Contact</span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="contact-title">
              {t('contactTitle')}
            </h1>
            <p className="text-sm sm:text-base text-text_secondary mt-4">
              {t('contactSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-lg sm:text-xl lg:text-lg sm:text-xl lg:text-2xl font-bold font-heading text-text_primary mb-4 sm:mb-6 sm:mb-8">
                {x.coordonnees}
              </h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4 p-5 bg-secondary/30 rounded-2xl">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text_primary">{t('address')}</h3>
                    <p className="text-text_secondary">Rue St Nicolas 227</p>
                    <p className="text-text_secondary">Belgique</p>
                  </div>
                </div>
                
                {/* Phone 1 */}
                <a
                  href="tel:0465710117"
                  className="flex items-start gap-4 p-5 bg-secondary/30 rounded-2xl hover:bg-secondary/50 transition-colors group"
                  data-testid="contact-phone-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:bg-primary group-hover:text-white transition-colors">
                    <Phone className="w-6 h-6 text-primary group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text_primary">{t('callUs')}</h3>
                    <p className="text-base font-medium text-primary">0465 71 01 17</p>
                  </div>
                </a>
                
                {/* Phone 2 */}
                <a
                  href="tel:0465288417"
                  className="flex items-start gap-4 p-5 bg-secondary/30 rounded-2xl hover:bg-secondary/50 transition-colors group"
                  data-testid="contact-phone-2"
                >
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:bg-primary group-hover:text-white transition-colors">
                    <Phone className="w-6 h-6 text-primary group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text_primary">{t('callUs')}</h3>
                    <p className="text-base font-medium text-primary">0465 28 84 17</p>
                  </div>
                </a>
                
                {/* Email */}
                <a
                  href="mailto:contact@afrionebd.eu"
                  className="flex items-start gap-4 p-5 bg-secondary/30 rounded-2xl hover:bg-secondary/50 transition-colors group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:bg-primary group-hover:text-white transition-colors">
                    <Mail className="w-6 h-6 text-primary group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text_primary">Email</h3>
                    <p className="text-base font-medium text-primary">contact@afrionebd.eu</p>
                  </div>
                </a>
              </div>
              
              {/* Hours */}
              <div className="mt-8 p-6 bg-primary/5 rounded-2xl">
                <h3 className="font-semibold text-text_primary mb-4">{x.horaires}</h3>
                <ul className="space-y-2 text-text_secondary">
                  <li className="flex justify-between">
                    <span>{x.semaine}</span>
                    <span className="font-medium text-text_primary">9h30 - 20h</span>
                  </li>
                  <li className="flex justify-between">
                    <span>{x.weekend}</span>
                    <span className="font-medium text-text_primary">10h - 19h30</span>
                  </li>
                </ul>
              </div>
            </motion.div>
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white p-8 rounded-3xl shadow-xl">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold font-heading text-text_primary mb-4 sm:mb-6">
                  {x.envoyezMessage}
                </h2>
                
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-secondary mx-auto flex items-center justify-center mb-4">
                      <Check className="w-8 h-8 text-secondary_hover" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-text_primary mb-2">{t('messageSent')}</h3>
                    <p className="text-text_secondary">{x.reponse}</p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="mt-6 px-6 py-2 bg-secondary/50 text-text_primary rounded-full font-medium hover:bg-secondary transition-colors"
                    >
                      {x.autreMessage}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-text_primary mb-2">
                        {t('fullName')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        data-testid="contact-name-input"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text_primary mb-2">
                        {t('email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        data-testid="contact-email-input"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-text_primary mb-2">
                        {t('phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-text_primary mb-2">
                        {t('yourMessage')} *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        t('processing')
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {t('sendMessage')}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
